import React, { useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { fmt, fmtDateShort } from '../lib/format';
import { Bank, Plus, Warning } from '@phosphor-icons/react';

export default function Banks() {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [depositing, setDepositing] = useState(null);
  const [editingBalance, setEditingBalance] = useState(null);
  const [history, setHistory] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const r = await api.get('/banks'); setBanks(r.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5 fade-in" data-testid="banks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">银行账户</h1>
          <p className="text-sm text-slate-400 mt-0.5">WW / DG 等银行通道储备管理</p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-secondary inline-flex items-center gap-2 text-sm" data-testid="add-bank-btn">
          <Plus size={16} /> 新增银行
        </button>
      </div>

      {loading && <div className="card-base p-12 text-center text-slate-500">加载中…</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {banks.map((b) => {
          const low = b.balance < b.low_balance_threshold;
          return (
            <div key={b.id} className={`card-base p-5 ${low ? 'border-amber-500/40' : ''}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-indigo-500/20 to-violet-500/10 border border-indigo-500/30 flex items-center justify-center">
                    <Bank size={22} weight="duotone" className="text-indigo-300" />
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-white">{b.name}</div>
                    <div className="text-xs text-slate-500">手续费 {b.bank_fee_percent}% · 客户费 {b.customer_fee_ref}%</div>
                  </div>
                </div>
                {low && <Warning size={20} weight="fill" className="text-amber-400" />}
              </div>

              <div>
                <div className="label-tiny mb-1">账面余额</div>
                <div className="font-num text-3xl font-semibold text-white">
                  $ {fmt(b.balance)} <span className="text-sm text-slate-500">USD</span>
                </div>
                {low && <div className="text-xs text-amber-400 mt-1">⚠️ 余额低于 ${fmt(b.low_balance_threshold)}</div>}
                <div className="text-xs text-slate-500 mt-1 font-num">最后对账:{fmtDateShort(b.last_reconciled_at)}</div>
              </div>

              <div className="flex flex-wrap gap-2 mt-5">
                <button onClick={() => setDepositing(b)} className="btn-primary text-sm" data-testid={`deposit-${b.name}`}>充值</button>
                <button onClick={() => setEditingBalance(b)} className="btn-secondary text-sm">设置初始</button>
                <button onClick={async () => {
                  const r = await api.get(`/banks/${b.id}/history`); setHistory({ bank: b, ...r.data });
                }} className="btn-ghost text-sm">历史</button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Deposit dialog */}
      {depositing && <DepositDialog bank={depositing} onClose={() => setDepositing(null)} onDone={load} />}

      {/* Edit initial balance */}
      {editingBalance && (
        <BalanceDialog bank={editingBalance} onClose={() => setEditingBalance(null)} onDone={load} />
      )}

      {/* History */}
      {history && <HistoryDialog data={history} onClose={() => setHistory(null)} />}

      {/* Create bank */}
      {creating && <CreateBankDialog onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function DepositDialog({ bank, onClose, onDone }) {
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const num = parseFloat(amount || 0);
  const fee = num * bank.bank_fee_percent / 100;
  const net = num - fee;
  const newBalance = bank.balance + net;

  const submit = async () => {
    setError(''); setLoading(true);
    try {
      await api.post('/banks/deposit', { bank_id: bank.id, amount: num });
      onDone(); onClose();
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };

  return (
    <Modal title={`充值到 ${bank.name}`} onClose={onClose}>
      <div className="space-y-4">
        <div>
          <label className="label-tiny block mb-2">充值金额 (USD)</label>
          <input type="number" step="0.01" className="input-base font-num text-lg" placeholder="5000"
                 value={amount} onChange={e => setAmount(e.target.value)} data-testid="deposit-amount" />
        </div>
        {num > 0 && (
          <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-4 space-y-2 text-sm">
            <Row label="银行扣手续费" value={`$${fmt(fee)} (${bank.bank_fee_percent}%)`} />
            <Row label="实际到账" value={`$${fmt(net)}`} highlight />
            <div className="h-px bg-slate-700/50" />
            <Row label="充值后余额" value={`$${fmt(newBalance)}`} big highlight />
          </div>
        )}
        {error && <div className="text-sm text-red-400">{error}</div>}
        <div className="flex gap-3">
          <button onClick={onClose} className="btn-secondary flex-1">取消</button>
          <button onClick={submit} disabled={loading || !num} className="btn-primary flex-1" data-testid="confirm-deposit">
            {loading ? '提交…' : '确认充值'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function BalanceDialog({ bank, onClose, onDone }) {
  const [balance, setBalance] = useState(bank.balance.toString());
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      await api.put(`/banks/${bank.id}/initial-balance`, { balance: parseFloat(balance) });
      onDone(); onClose();
    } finally { setLoading(false); }
  };
  return (
    <Modal title={`设置 ${bank.name} 当前余额`} onClose={onClose}>
      <p className="text-xs text-slate-400 mb-3">⚠️ 直接设置余额,通常用于初始化。日常充值请用「充值」按钮。</p>
      <div className="space-y-3">
        <input type="number" step="0.01" className="input-base font-num text-lg" placeholder="2000"
               value={balance} onChange={e => setBalance(e.target.value)} />
        <div className="flex gap-3">
          <button onClick={onClose} className="btn-secondary flex-1">取消</button>
          <button onClick={submit} disabled={loading} className="btn-primary flex-1">{loading ? '保存…' : '保存'}</button>
        </div>
      </div>
    </Modal>
  );
}

function HistoryDialog({ data, onClose }) {
  return (
    <Modal title={`${data.bank.name} 历史记录`} onClose={onClose} wide>
      <div className="space-y-4 max-h-[60vh] overflow-y-auto">
        <div>
          <div className="label-tiny mb-2">充值记录</div>
          {data.deposits.length === 0 && <div className="text-sm text-slate-500">暂无</div>}
          {data.deposits.map(d => (
            <div key={d.id} className="p-3 bg-slate-900/40 rounded-lg border border-slate-800 mb-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-300 font-num">{fmtDateShort(d.created_at)}</span>
                <span className="text-xs text-slate-500">{d.type === 'initial' ? '初始化' : '充值'}</span>
              </div>
              <div className="font-num mt-1">
                ${fmt(d.gross_amount)} - 费 ${fmt(d.bank_fee_amount)} = <span className="text-emerald-300">${fmt(d.net_amount)}</span>
              </div>
              <div className="text-xs text-slate-500 font-num">余额 → ${fmt(d.balance_after)}</div>
            </div>
          ))}
        </div>
        <div>
          <div className="label-tiny mb-2">结算记录</div>
          {data.reconciliations.length === 0 && <div className="text-sm text-slate-500">暂无</div>}
          {data.reconciliations.map(r => (
            <div key={r.id} className="p-3 bg-slate-900/40 rounded-lg border border-slate-800 mb-2 text-sm">
              <div className="text-xs text-slate-500 font-num">{fmtDateShort(r.created_at)} by {r.created_by}</div>
              <div className="font-num mt-1">
                账面 ${fmt(r.book_balance)} → 实际 ${fmt(r.actual_balance)}
              </div>
              <div className="font-num text-xs">
                消耗 ${fmt(r.consumed)} · 银行利润估算 <span className="text-emerald-400">${fmt(r.bank_profit_estimate)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}

function CreateBankDialog({ onClose, onDone }) {
  const [name, setName] = useState('');
  const [bf, setBf] = useState('');
  const [cf, setCf] = useState('');
  const [bal, setBal] = useState('0');
  const submit = async () => {
    await api.post('/banks', { name, bank_fee_percent: parseFloat(bf), customer_fee_ref: parseFloat(cf), initial_balance: parseFloat(bal || 0) });
    onDone();
  };
  return (
    <Modal title="新增银行通道" onClose={onClose}>
      <div className="space-y-3">
        <input type="text" placeholder="名称(例如 GG)" className="input-base" value={name} onChange={e=>setName(e.target.value)} />
        <input type="number" step="0.01" placeholder="银行手续费 % (例如 0.5)" className="input-base font-num" value={bf} onChange={e=>setBf(e.target.value)} />
        <input type="number" step="0.01" placeholder="收客户参考费率 % (例如 1.5)" className="input-base font-num" value={cf} onChange={e=>setCf(e.target.value)} />
        <input type="number" step="0.01" placeholder="初始余额 USD" className="input-base font-num" value={bal} onChange={e=>setBal(e.target.value)} />
        <div className="flex gap-3">
          <button onClick={onClose} className="btn-secondary flex-1">取消</button>
          <button onClick={submit} className="btn-primary flex-1">创建</button>
        </div>
      </div>
    </Modal>
  );
}

function Modal({ children, title, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className={`card-base p-6 w-full ${wide ? 'max-w-2xl' : 'max-w-md'}`}>
        <h3 className="text-lg font-semibold text-white mb-4">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function Row({ label, value, highlight, big }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-slate-400">{label}</span>
      <span className={`font-num ${big ? 'text-lg font-semibold' : ''} ${highlight ? 'text-emerald-300' : 'text-white'}`}>{value}</span>
    </div>
  );
}
